/**
 * The DESIGN §6 lifecycle colour for a status, factored out of `ui/badge.tsx` so that
 * file exports components only — react-refresh/only-export-components needs that to
 * do its job, and a bar chart or legend swatch that wants the colour without a dot or
 * chip attached (`Ingest.tsx`'s runs table) has somewhere to get it from that isn't a
 * component module.
 *
 * Amber is waiting on a human, green has been through review, violet is folded into a
 * cluster, red failed, sky is mid-pipeline, grey is out of the bank.
 */
const TONE = {
  attention: "bg-bank-attention-wash text-bank-attention-ink",
  good: "bg-bank-good-wash text-bank-good-ink",
  dup: "bg-bank-dup-wash text-bank-dup-ink",
  bad: "bg-bank-bad-wash text-bank-bad-ink",
  pipe: "bg-bank-pipe-wash text-bank-pipe-ink",
  inert: "bg-bank-inert-wash text-bank-inert-ink",
} as const;

const DOT = {
  attention: "bg-bank-attention",
  good: "bg-bank-good",
  dup: "bg-bank-dup",
  bad: "bg-bank-bad",
  pipe: "bg-bank-pipe",
  inert: "bg-bank-inert",
} as const;

export type Tone = keyof typeof TONE;

/** Question statuses (§6), job statuses (§10.2), and the two dedup verdicts. */
const STATUS: Record<string, Tone> = {
  raw: "inert",
  extracted: "inert",
  split_checked: "inert", // §17.11.4: extracted, looked at by the split stage — still pre-enrichment
  enriched: "pipe",
  embedded: "pipe",
  dedup_checked: "pipe",
  pending_review: "attention",
  stale: "attention",
  approved: "good",
  duplicate: "dup",
  rejected: "inert",
  deprecated: "inert",
  failed: "bad",
  queued: "inert",
  running: "pipe",
  done: "good",
  cancelled: "inert",
  distinct: "inert",
  // question_splits.status (§17.11.2): a proposal waits on a person, applied has been through one.
  proposed: "attention",
  applied: "good",
  kept: "inert",
  exempt: "inert",
  // Job descriptions (DESIGN §20.4): an upload being read is machine work, a JD in review waits on a
  // person, a published one has been through one, and archived or discarded ones are out of use.
  analysing: "pipe",
  analysed: "good",
  in_review: "attention",
  active: "good",
  archived: "inert",
  discarded: "inert",
  gap: "attention",
};

export function statusTone(value: string): Tone {
  return STATUS[value] ?? "inert";
}

export function statusChipClass(value: string): string {
  return TONE[statusTone(value)];
}

/**
 * Estimated speech time against the configured limit (DESIGN §17.11.6): green fits, amber is within
 * half again of it, red is well over. Here rather than in the chip's module because
 * react-refresh/only-export-components wants a component file to export components only.
 */
export function speechTone(seconds: number, limit: number): Tone {
  if (seconds <= limit) return "good";
  return seconds <= limit * 1.5 ? "attention" : "bad";
}

/** A tone's chip classes for something that is not a status — the speech-time chip (§17.11.6). */
export function toneChipClass(tone: Tone): string {
  return TONE[tone];
}

/** The dot's fill class for a status, with no dot attached. */
export function statusFillClass(value: string): string {
  return DOT[statusTone(value)];
}

/**
 * Where a picked question came from (DESIGN §20.9): the JD's own question, how it became the JD's,
 * or the related pool. Not a lifecycle state, so not in `STATUS` — but the same washes, so a source
 * chip sits beside a status chip without inventing a colour (BRANDING §1).
 */
const SOURCE_TONE: Record<string, string> = {
  jd_authored: "bg-bank-accent-wash text-bank-accent-ink",
  jd_generated: TONE.dup,
  jd_matched: TONE.pipe,
  related: TONE.inert,
};

export function sourceChipClass(source: string): string {
  return SOURCE_TONE[source] ?? TONE.inert;
}
